import { api } from './api';

export interface Contact {
  id: string;
  workspaceId: string;
  accountId: string | null;
  displayName: string;
  familyName: string | null;
  givenName: string | null;
  familyNameKana: string | null;
  givenNameKana: string | null;
  email: string | null;
  emailVerified: boolean;
  phone: string | null;
  mobile: string | null;
  title: string | null;
  department: string | null;
  countryCode: string | null;
  language: string | null;
  linkedinUrl: string | null;
  source: string | null;
  ownerUserId: string | null;
  lastTouchedAt: string | null;
  healthScore: number | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Canonical fields the CSV bulk-import endpoint can write to. Keep this
 * union in sync with `CanonicalField` in
 * `backend/src/modules/contact/contact-bulk-import.service.ts`.
 */
export type BulkImportField =
  | 'family_name'
  | 'given_name'
  | 'family_name_kana'
  | 'given_name_kana'
  | 'email'
  | 'phone'
  | 'mobile'
  | 'title'
  | 'department'
  | 'company'
  | 'country_code'
  | 'jp_corp_number'
  | 'display_name'
  | 'ignore';

export interface BulkImportResult {
  imported: number;
  skipped: number;
  errors: Array<{ row: number; reason: string }>;
  previewMapping: Record<string, BulkImportField>;
  preview: {
    headers: string[];
    rows: Array<Record<string, string>>;
  };
}

export const contactsApi = {
  list: async (params: { workspaceId: string; accountId?: string; search?: string; limit?: number; offset?: number }) => {
    const r = await api.get<{ items: Contact[]; total: number }>('/api/contacts', { params });
    return r.data;
  },
  create: async (input: Partial<Contact> & { workspaceId: string; displayName: string }) => {
    const r = await api.post<Contact>('/api/contacts', input);
    return r.data;
  },
  get: async (id: string) => {
    const r = await api.get<Contact>(`/api/contacts/${id}`);
    return r.data;
  },
  update: async (id: string, patch: Partial<Contact>) => {
    const r = await api.patch<Contact>(`/api/contacts/${id}`, patch);
    return r.data;
  },
  remove: async (id: string) => {
    await api.delete(`/api/contacts/${id}`);
  },
  searchSemantic: async (params: { workspaceId: string; q: string; limit?: number }) => {
    const r = await api.get<{ items: (Contact & { score: number })[]; semantic: boolean }>(
      '/api/contacts/search/semantic',
      { params },
    );
    return r.data;
  },
  reindex: async (workspaceId: string) => {
    const r = await api.post<{ indexed: number; enabled: boolean }>(
      '/api/contacts/reindex',
      null,
      { params: { workspaceId } },
    );
    return r.data;
  },
  /**
   * Bulk-import contacts from a CSV file. Two-phase usage:
   *   1. Call with `mapping=undefined` to get back the heuristic mapping
   *      + a 10-row preview. (The server doesn't actually short-circuit
   *      a "preview-only" call — it does the import in one transaction.
   *      Callers that want preview-then-confirm should run the second
   *      call with `mapping` set.)
   *   2. Call again with `mapping` set to the user-edited column→field
   *      map; the server applies it as overrides on top of the heuristic.
   */
  bulkImport: async (params: {
    workspaceId: string;
    file: File;
    mapping?: Record<string, BulkImportField>;
  }): Promise<BulkImportResult> => {
    const fd = new FormData();
    fd.append('file', params.file);
    fd.append('workspaceId', params.workspaceId);
    if (params.mapping) {
      fd.append('mapping', JSON.stringify(params.mapping));
    }
    const r = await api.post<BulkImportResult>('/api/contacts/bulk-import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return r.data;
  },
};
